import { IconName } from '@lyse-as/formds-icons';
import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State } from '@stencil/core';
import clsx from 'clsx';
import { HTMLInputTypeAttribute } from 'react';
import { v4 as uuidv4 } from 'uuid';

import store from '@/store';

export type FdsInputVariant = 'primary' | 'warning' | 'error' | 'success';
export type FdsInputAutocomplete =
  | 'off'
  | 'on'
  | 'name'
  | 'email'
  | 'username'
  | 'new-password'
  | 'current-password'
  | 'street-address'
  | 'country'
  | 'postal-code'
  | 'tel';

export type FdsInputType = Extract<
  HTMLInputTypeAttribute,
  'text' | 'password' | 'date' | 'search' | 'email' | 'number' | 'tel'
>;

export interface FdsInputProps {
  label: string;
  name: string;
  inputId?: string;
  description?: string;
  placeholder?: string;
  variant?: FdsInputVariant;
  leftIcon?: IconName;
  type?: FdsInputType;
  autocomplete?: FdsInputAutocomplete;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  maxLength?: number;
  minLength?: number;
  max?: number;
  min?: number;
}

@Component({
  tag: 'fds-input',
  styleUrl: 'styles/input.scss',
  scoped: true,
})
export class Input implements FdsInputProps {
  private descriptionRef: HTMLFdsDescriptionElement;

  @Element() host: HTMLFdsInputElement;

  @State() showPassword = false;

  /**
   * The label of the input
   */
  @Prop() label: string;

  /**
   * Sets the name attribute for the input
   */
  @Prop() name!: string;

  /**
   * The value of the input
   */
  @Prop({ mutable: true }) value: string;

  /**
   * If set, will render a fixed unchangeable value inside of the input
   */
  @Prop() fixedValue: string;

  /**
   * Sets the id attribute of the input. Auto-generated if none is provided
   */
  @Prop() inputId = uuidv4();

  /**
   * Sets the id attribute of the description. Auto-generated if none is provided
   */
  @Prop() descriptionId = uuidv4();

  /**
   * Add a description to the input. Is rendered underneath the input field
   */
  @Prop() description: string;

  /**
   * Sets the placeholder of the input
   */
  @Prop() placeholder: string;

  /**
   * Sets the variant of input to use
   */
  @Prop() variant: FdsInputVariant = 'primary';

  /**
   * If true, an icon will be rendered inside of the input, to the left of the actual text
   */
  @Prop() leftIcon: IconName;

  /**
   * Sets the input type, i.e. "text", "password", "email" etc
   */
  @Prop() type: FdsInputType;

  /**
   * Sets the autocomplete attribute for the input
   */
  @Prop() autocomplete: FdsInputAutocomplete = 'off';

  /**
   * Sets the required attribute for the input
   */
  @Prop() required = false;

  /**
   * Sets the readonly attribute for the input
   */
  @Prop() readonly = false;

  /**
   * Sets the max length of the text inside the input
   */
  @Prop() maxLength: number;

  /**
   * Sets the min length of the text inside the input
   */
  @Prop() minLength: number;

  /**
   * Sets the max number inside the input
   */
  @Prop() max: number;

  /**
   * Sets the min number inside the input
   */
  @Prop() min: number;

  /**
   * If true, the input is disabled, and users cannot interact with it
   */
  @Prop() disabled = false;

  /**
   * If true, hides the label in the DOM. It will still be rendered in the DOM, but not visible
   */
  @Prop() hideLabel = false;

  /**
   * If false, sets the tabIndex attribute to -1, meaning the element can't receive focus
   */
  @Prop() focusable = true;

  /**
   * Emitted when the value of the input changes
   */
  @Event() fdsChange: EventEmitter<string>;

  /**
   * Emitted when the input is clicked
   */
  @Event() fdsClick: EventEmitter;

  /**
   * Emitted when the search query changes
   */
  @Event() fdsFocusin: EventEmitter<FocusEvent>;

  /**
   * Gets the height of the description text
   * @returns A number in pixels determining the height of the description
   */
  @Method()
  async getDescriptionHeight() {
    if (this.descriptionRef) {
      const rect = this.descriptionRef.getBoundingClientRect();
      return rect.height;
    }

    return 0;
  }

  private handleOnInput = (event: InputEvent) => {
    this.value = event.target ? (event.target as HTMLInputElement).value : undefined;
    this.fdsChange.emit(this.value);
  };

  private handleFocusIn = (event: FocusEvent) => {
    this.fdsFocusin.emit(event);
  };

  private handleClick = (event: MouseEvent) => {
    event.preventDefault();
    this.fdsClick.emit();
  };

  private handleClearSearchQuery = () => {
    this.value = '';
    this.fdsChange.emit('');
  };

  private handleShowHidePasswordClick = () => {
    this.showPassword = !this.showPassword;
  };

  private renderShowHidePassword() {
    return (
      <fds-icon-button
        icon={this.showPassword ? 'visibility_on' : 'visibility_off'}
        variant="tertiary"
        disabled={this.disabled}
        onClick={this.handleShowHidePasswordClick}
      />
    );
  }

  private renderClearSearchQuery() {
    return (
      <fds-icon-button icon="close" variant="tertiary" disabled={this.disabled} onClick={this.handleClearSearchQuery} />
    );
  }

  private renderCalendarIcon() {
    return <fds-icon-button icon="calendar" variant="tertiary" disabled={this.disabled} onClick={this.handleClick} />;
  }

  private getRenderedType() {
    if (this.type === 'password' && this.showPassword) {
      return 'text';
    }

    if (this.type === 'date') {
      return 'text';
    }

    return this.type;
  }

  render() {
    const {
      variant,
      disabled,
      hideLabel,
      label,
      inputId,
      maxLength,
      minLength,
      max,
      min,
      required,
      leftIcon,
      focusable,
      fixedValue,
      name,
      placeholder,
      value,
      autocomplete,
      description,
      descriptionId,
      readonly,
      handleClick,
      handleOnInput,
      handleFocusIn,
    } = this;

    const containerClasses = clsx({
      [`fds-input--${variant}`]: variant,
      'fds-input--disabled': disabled,
      [store.mode ?? '']: true,
    });

    const headerClasses = clsx({
      'fds-input__header-container': true,
      'fds-input__header-container--hidden': hideLabel,
    });

    const inputContainerClasses = clsx({
      'fds-input__input-container': true,
      'fds-input__input-container--not-focusable': !focusable,
    });

    return (
      <Host class={containerClasses}>
        <div class={headerClasses}>
          {label && <label htmlFor={inputId}>{label}</label>}
          {required && <span class="fds-input__required">*</span>}
        </div>
        <div class={inputContainerClasses}>
          {leftIcon && <fds-icon name={leftIcon} />}
          {fixedValue && <span class="fds-input__fixed-value">{fixedValue}</span>}
          <input
            type={this.getRenderedType()}
            id={inputId}
            name={name}
            placeholder={placeholder}
            value={value}
            maxLength={maxLength}
            minLength={minLength}
            max={max}
            min={min}
            disabled={disabled}
            autoComplete={autocomplete}
            required={required}
            readonly={readonly}
            aria-label={hideLabel ? label : undefined}
            onInput={handleOnInput}
            onClick={handleClick}
            aria-describedby={descriptionId}
            onFocusin={handleFocusIn}
          />
          {this.type === 'password' && this.renderShowHidePassword()}
          {this.type === 'date' && this.renderCalendarIcon()}
          {this.type === 'search' && value && this.renderClearSearchQuery()}
        </div>
        <fds-description
          ref={(el) => (this.descriptionRef = el)}
          descriptionId={descriptionId}
          description={description}
          variant={variant}
        >
          <slot name="custom-description" slot="custom-description" />
        </fds-description>
      </Host>
    );
  }
}
